import React, { useEffect, useRef, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { NavLink, Outlet, useLocation } from 'react-router-dom';
import classNames from 'classnames';
import { useIntl } from 'react-intl';
import moment from 'moment';
import { Icons } from '../base/BootstrapComponents/BootstrapComponents';
import Loader from '../base/Loader/Loader';
import {
  getPathNameWithOutParameters,
  responseLogWithNotification,
  useUpdatePeriod,
} from '../../utils/utils';
import { getActiveBlockName, sortLinksFunction } from '../../utils/layout-utils';
import { BUILD_VERSION, STATUS_ENUM } from '../../configs';
import Notifications from '../../modules/Notifications/components/Notifications';
import GlobalConfirmModal from '../../modules/System/components/GlobalConfirmModal';
import { getSystemData } from '../../modules/System/reducer';
import Dropdown from '../base/Dropdown/Dropdown';
import DateTime from './DateTime';
import Tooltip from '../base/Tooltip/Tooltip';
import UpdatePeriodSelector from './UpdatePeriodSelector';
import { getCertStatus, statusClassName, textStatusColorFormatter } from '../../utils/formatters';
import { loadStatus } from '../../modules/System/actions';
import { getWSData } from '../../modules/WebSocket/reducer';
import { getLoginProcess } from '../../modules/Authorized/reducer';

const formatUptime = seconds => {
  if (!Number.isFinite(seconds)) {
    return '';
  }
  const totalSeconds = Math.floor(seconds);
  const days = Math.floor(totalSeconds / 86400);
  const hours = Math.floor((totalSeconds % 86400) / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const secs = totalSeconds % 60;
  const time = [hours, minutes, secs].map(value => String(value).padStart(2, '0')).join(':');
  return days > 0 ? `${days}d ${time}` : time;
};

export default function Layout({ currentRoutes, currentRoutesByMenu, children }) {
  const dispatch = useDispatch();
  const location = useLocation();
  const intl = useIntl();
  const { formatMessage } = intl;
  const uptimeSyncRef = useRef(null);
  const [currentUptimeSeconds, setCurrentUptimeSeconds] = useState(null);
  const loadSystemStatus = () => {
    const p1 = dispatch(loadStatus()).catch(responseLogWithNotification);
    return Promise.all([p1]);
  };
  const { updatePeriod } = useSelector(getSystemData);
  const [setPeriod] = useUpdatePeriod(loadSystemStatus, updatePeriod);
  useEffect(() => setPeriod(updatePeriod), [updatePeriod]);
  useEffect(() => {
    window.addEventListener('scroll', window.customEventListener.emit, { capture: true });
    window.addEventListener('mousemove', window.customEventListener.emit, { capture: true });
    window.addEventListener('mouseup', window.customEventListener.emit, { capture: true });
    window.addEventListener('mousedown', window.customEventListener.emit, { capture: true });
    return () => {
      window.removeEventListener('scroll', window.customEventListener.emit);
      window.removeEventListener('mousemove', window.customEventListener.emit);
      window.removeEventListener('mouseup', window.customEventListener.emit);
      window.removeEventListener('mousedown', window.customEventListener.emit);
    };
  }, []);

  useEffect(() => {
    setMenuOpenState(false);
  }, [location.pathname]);

  const currentPathName = getPathNameWithOutParameters(location.pathname);
  const [activeBlockName, setActiveBlockName] = useState(getActiveBlockName(currentPathName, currentRoutes));
  const [menuOpenState, setMenuOpenState] = useState(false);
  const loginProcess = useSelector(getLoginProcess);
  const { loadApp, status } = useSelector(getSystemData);
  const { latitude, longitude, gps_status } = useSelector(getWSData);

  useEffect(() => {
    const uptimeSeconds = Number(status.uptime_seconds);
    if (!Number.isFinite(uptimeSeconds)) {
      uptimeSyncRef.current = null;
      setCurrentUptimeSeconds(null);
      return undefined;
    }

    uptimeSyncRef.current = {
      seconds: uptimeSeconds,
      syncedAt: Date.now(),
    };
    setCurrentUptimeSeconds(uptimeSeconds);

    const intervalId = window.setInterval(() => {
      if (!uptimeSyncRef.current) {
        return;
      }
      const elapsedSeconds = (Date.now() - uptimeSyncRef.current.syncedAt) / 1000;
      setCurrentUptimeSeconds(uptimeSyncRef.current.seconds + elapsedSeconds);
    }, 1000);

    return () => {
      window.clearInterval(intervalId);
    };
  }, [status.uptime_seconds]);

  const handleClickMenuBlock = name => () => {
    let newName = name;
    if (activeBlockName === name) {
      newName = null;
    }
    setActiveBlockName(newName);
  };

  const renderMenuLink = (prop, key, isNested = true) => {
    if (prop.hideOnMenu || prop.redirect) {
      return null;
    }
    let className = prop.className || '';
    const isChildren = Boolean(prop.children && prop.children.length);
    const currentPathName = getPathNameWithOutParameters(location.pathname);
    const newActiveBlockName = getActiveBlockName(currentPathName, currentRoutes);
    if (activeBlockName !== newActiveBlockName) {
      setActiveBlockName(newActiveBlockName);
    }

    const pathName = getPathNameWithOutParameters(prop.path);
    const addProps = {};
    if (isNested) {
      addProps.onClick = handleClickMenuBlock(prop.name);
    }
    if (!isNested) {
      className = classNames(className, 'nested');
    }
    const isOpen = prop.name === activeBlockName;
    const isSelected = (currentPathName.includes(pathName) && pathName !== '/') || (currentPathName === pathName);
    const { MenuComponent = null } = prop;
    return (
      <div className={classNames('bd-toc-item', prop.className || '')} key={key}>
        <NavLink to={pathName} {...addProps} className={classNames('menu-link', className, { selected: isSelected || isOpen })}>
          {isNested && <Icons className="menu-item-icon" icon={prop.icon} customIcon={!!prop.customIcon} />}
          {MenuComponent ? <MenuComponent prop={prop} isOpen={isOpen} /> : <span className="nav-link-text">{formatMessage({ id: prop.name })}</span>}
          {isChildren && <Icons icon={isOpen ? 'caret-up-fill' : 'caret-down-fill'} className="nested-arrow" width={12} height={12} />}
        </NavLink>
        {isChildren && isOpen
          ? (
            <ul className="nav nested sub-nav-group">
              {prop.children.sort(sortLinksFunction).map((prop, key) => renderMenuLink(prop, key, false))}
            </ul>
          )
          : null}
      </div>
    );
  };

  const sortedRoutes = currentRoutesByMenu.sort(sortLinksFunction);
  const anchorElement = useRef(null);
  const isSelectedProfile = currentPathName.includes('/profile') || currentPathName === '/profile';

  const complexAddress = status.address || '';
  const complexNumber = status.serial_number || '';
  const complexName = status.name || '';
  const gpsText = latitude && longitude
    ? `${Number(latitude).toFixed(6)} ${Number(longitude).toFixed(6)}`
    : formatMessage({ id: 'gps.dont.load' });
  const gpsStatus = !Number.isNaN(gps_status) ? gps_status : STATUS_ENUM.ERROR;
  const reboot = () => {};
  const tz = status.time_zone ? status.time_zone : 'Europe/Moscow';
  const globalTime = status.global_time ? moment(status.global_time) : null; // moment()
  const startTime = globalTime && status.start_date ? moment(status.start_date) : null;
  const verificationEndDate = status.verification_end_date ? moment(status.verification_end_date) : null;
  const startDiff = startTime && globalTime ? globalTime.diff(startTime, 'h') : null;
  const uptime = Number.isFinite(currentUptimeSeconds)
    ? formatUptime(currentUptimeSeconds)
    : `${startDiff || 0}h`;
  const vDiff = verificationEndDate && globalTime ? verificationEndDate.diff(globalTime, 'd') : null;
  return (
    <div className={classNames('main-layout')}>
      <Notifications />
      <Loader loading={loginProcess || loadApp} global hideScreen={loadApp} />
      <div className="app-container">
        <div className={classNames('app-left-column', { active: menuOpenState })}>
          <div className="toggle-button-wrapper" onClick={() => { setMenuOpenState(!menuOpenState); }}>
            <Icons customIcon className={classNames('toggle-button', { 'arrow-forward': !menuOpenState }, { 'arrow-back': menuOpenState })} icon="arrow-forward" />
          </div>
          <div className="app-menu">
            <nav className="bd-links">
              <ul className="nav">
                {sortedRoutes.map(renderMenuLink)}
                <div className="menu-hr" />
                <div className="bd-toc-item">
                  {/* <NavLink className={classNames('menu-link menu-profile', { selected: isSelectedProfile })} to="/profile"> */}
                  {/*  <Icons customIcon className="menu-item-icon" icon="profile" /> */}
                  {/*  <span className="nav-link-text">{formatMessage({ id: 'menu.profile' })}</span> */}
                  {/* </NavLink> */}
                  <Dropdown usePortal>
                    <Dropdown.Toggle>
                      <div className="menu-link logout-link" to="/logout" ref={anchorElement}>
                        <Icons customIcon className="menu-item-icon" icon="exit" />
                        <span className="nav-link-text">{formatMessage({ id: 'menu.logout' })}</span>
                      </div>
                    </Dropdown.Toggle>
                    <Dropdown.Menu
                      className="logout-menu-wrapper"
                      anchorElement={anchorElement}
                      classNamePortal="logout-menu-portal"
                      useResizeContent={false}
                    >
                      <div className="bd-toc-item">
                        {/* <span className="menu-link menu-profile" onClick={() => reboot()}> */}
                        {/*  <Icons customIcon className="menu-item-icon" icon="reboot" /> */}
                        {/*  <span className="nav-link-text">{formatMessage({ id: 'menu.reload' })}</span> */}
                        {/* </span> */}
                        <NavLink className="menu-link menu-profile" to="/logout">
                          <Icons customIcon className="menu-item-icon" icon="exit" />
                          <span className="nav-link-text">{formatMessage({ id: 'menu.logout' })}</span>
                        </NavLink>
                      </div>
                    </Dropdown.Menu>
                  </Dropdown>
                </div>
                <div className="version-number">{BUILD_VERSION}/{status.version_beackend || ''}</div>
              </ul>
            </nav>
          </div>
        </div>
        <div className="app-content-wrapper custom-scroll-theme">
          <div className="app-content">
            <div className="app-bar">
              <div className="logo-block">
                <div className="logo" />
                <div className="logo-name" />
              </div>
              <div className="complex-time-block">
                <div className="complex-operating-hours">
                  {formatMessage({ id: 'work.time' }, { value: uptime })}
                </div>
                <div className="complex-verification-time">
                  {formatMessage({ id: 'verification' })} —&nbsp; {getCertStatus(vDiff)}
                </div>
              </div>
              <div className="complex-data-block">
                <div className="complex-address">
                  <Tooltip content={`${formatMessage({ id: 'address' })}: ${complexAddress}/${gpsText}`} />
                </div>
                <div className="complex-name">
                  <Tooltip content={<>{formatMessage({ id: 'serial' })}: {complexNumber}<span className="complex-name-delimiter">•</span>{formatMessage({ id: 'complex' })}: {complexName}</>} />
                </div>
              </div>
              <UpdatePeriodSelector />
              <DateTime initial={globalTime ? globalTime.toDate() : null} tz={tz} />
              {/* <DateTime initial={new Date('2024-06-16 23:59:55')} /> */}
              {/* <DateTime initial={new Date('2024-04-18 15:22:00')} /> */}
              <div className="gps-block">
                <Icons className={classNames('gps-icon', statusClassName(gpsStatus))} icon="gps" customIcon />
              </div>
            </div>
            <div className="app-content-children">
              <div className="page-wrapper custom-scroll-theme">
                <Outlet />
              </div>
            </div>
          </div>
        </div>
      </div>
      <GlobalConfirmModal />
    </div>
  );
}
